import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { usePopup } from "@/hooks/use-popup";
import { invalidateAfterEntryError, invalidateAfterEntryWrite } from "@/hooks/use-time-entries";
import { authClient } from "@/lib/auth-client";
import { alreadyUndone } from "@/lib/calendar/panel";
import { CALENDAR_SCOPES } from "@/lib/calendar/scopes";
import { POPUP_CONNECT_CALENDAR_PATH } from "@/lib/embedding";
import { isEmbedded } from "@/lib/popup-sign-in";
import { calendarKeys } from "@/lib/query";
import { ApiError, getJson, sendJson } from "@/lib/time/api-client";
import type {
  AcceptInput,
  CalendarAcceptResult,
  CalendarRuleView,
  CalendarStatusView,
  CalendarWeekView,
  DismissInput,
  RestoreInput,
  RuleInput,
  RuleUpdate,
  UndoAcceptInput,
} from "@/schemas/calendar";

/** Whether Microsoft sign-in is set up and the person's calendar is connected (Profile). */
export function useCalendarStatus() {
  return useQuery({
    queryKey: calendarKeys.status,
    queryFn: () => getJson<CalendarStatusView>("/api/calendar/status"),
    retry: retryServerErrors,
  });
}

/**
 * A refusal (403 while impersonating, 400) will not change on a retry; a 502 from Outlook may, once.
 * The panel's Retry button covers the rest.
 */
export function retryServerErrors(failureCount: number, err: Error): boolean {
  if (err instanceof ApiError && err.status < 500) return false;
  return failureCount < 1;
}

export function calendarWeekUrl(weekStart: string): string {
  return `/api/calendar/week?week=${encodeURIComponent(weekStart)}`;
}

/** The shown week's pending meetings with their suggestions (GET /api/calendar/week). */
export function useCalendarWeek(weekStart: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: calendarKeys.week(weekStart),
    queryFn: () => getJson<CalendarWeekView>(calendarWeekUrl(weekStart)),
    enabled: options.enabled ?? true,
    retry: retryServerErrors,
  });
}

/**
 * Refresh: reads the week from Outlook again (skipping the server's 60 s cache) and puts the
 * answer in place, so meetings added since the last read show up. Limited to a few a minute.
 */
export function useRefreshCalendarWeek() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (weekStart: string) =>
      getJson<CalendarWeekView>(`${calendarWeekUrl(weekStart)}&refresh=1`),
    onSuccess: (view, weekStart) => {
      queryClient.setQueryData(calendarKeys.week(weekStart), view);
      toast.success("Calendar refreshed", { id: "calendar-refreshed" });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : String(err));
    },
  });
}

export function sendAccept(input: AcceptInput): Promise<CalendarAcceptResult> {
  return sendJson<CalendarAcceptResult>("POST", "/api/calendar/accept", input);
}

/**
 * After Accepts: the grid, Entries, sign-offs and months re-read, and the week's suggestions too
 * (the server never returns an accepted meeting again). Once per batch, not once per meeting:
 * every suggestions read after a meeting write goes back to Outlook.
 */
export function invalidateAfterAccept(queryClient: QueryClient, weekStart: string) {
  return Promise.all([
    invalidateAfterEntryWrite(queryClient),
    invalidateCalendarWeek(queryClient, weekStart),
  ]);
}

/**
 * Accept one meeting. Toasts and the refresh are the caller's: one Accept says "Logged", "Accept N
 * matched" says "Logged N meetings" once at the end and refreshes once.
 */
export function useAcceptMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendAccept,
    // A 504 "This may have saved" can have written the entry, so the grid re-reads.
    onError: (err) => invalidateAfterEntryError(queryClient, err),
    retry: false,
  });
}

/**
 * Runs `accept` over `items` one after another (never a burst of parallel writes to the
 * warehouse). A failure does not stop the rest; each outcome is reported in order.
 */
export async function acceptInOrder<T, R>(
  items: T[],
  accept: (item: T) => Promise<R>,
): Promise<({ ok: true; item: T; result: R } | { ok: false; item: T; error: Error })[]> {
  const out: ({ ok: true; item: T; result: R } | { ok: false; item: T; error: Error })[] = [];
  for (const item of items) {
    try {
      out.push({ ok: true, item, result: await accept(item) });
    } catch (err) {
      out.push({ ok: false, item, error: err instanceof Error ? err : new Error(String(err)) });
    }
  }
  return out;
}

/**
 * Dismiss and Undo. The caller refreshes the week (`invalidateCalendarWeek`) in step with its own
 * row state, so a row never blinks out between the write and the re-read.
 */
export function useDismissMeeting() {
  return useMutation({
    mutationFn: (input: DismissInput) =>
      sendJson<{ eventKey: string }>("POST", "/api/calendar/dismiss", input),
    onSuccess: () => {
      toast.success("Dismissed", { id: "calendar-dismissed" });
    },
    onError: (err) => {
      toast.error(err.message);
    },
    retry: false,
  });
}

export function useRestoreMeeting() {
  return useMutation({
    mutationFn: (input: RestoreInput) =>
      sendJson<{ eventKey: string }>("POST", "/api/calendar/restore", input),
    onSuccess: () => {
      toast.success("Restored", { id: "calendar-restored" });
    },
    onError: (err) => {
      toast.error(err.message);
    },
    retry: false,
  });
}

/**
 * Undo an Accept: the entry goes and the meeting lists again. The caller refreshes in step with
 * its own row state, as with Dismiss, also on a failure: the delete can land and a later step fail.
 * "Nothing to undo" means an earlier Undo landed, so it counts as done.
 */
export function useUndoAccept() {
  return useMutation({
    mutationFn: async (input: UndoAcceptInput) => {
      try {
        await sendJson<{ eventKey: string }>("POST", "/api/calendar/undo-accept", input);
      } catch (err) {
        if (!alreadyUndone(err)) throw err;
      }
    },
    onSuccess: () => {
      toast.success("Entry removed", { id: "calendar-undone" });
    },
    onError: (err) => {
      toast.error(err.message);
    },
    retry: false,
  });
}

export function invalidateCalendarWeek(queryClient: QueryClient, weekStart: string) {
  return queryClient.invalidateQueries({ queryKey: calendarKeys.week(weekStart) });
}

export function useDisconnectCalendar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => sendJson<{ connected: false }>("POST", "/api/calendar/disconnect", {}),
    onSuccess: async () => {
      toast.success("Calendar disconnected");
      await queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    onError: (err) => {
      toast.error(err.message);
    },
    retry: false,
  });
}

/** The person's match rules, with each rule's task when still assigned (Profile). */
export function useCalendarRules(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: calendarKeys.rules,
    queryFn: () => getJson<CalendarRuleView[]>("/api/calendar/rules"),
    enabled: options.enabled ?? true,
    retry: retryServerErrors,
  });
}

/**
 * Add, edit and delete a rule. Each toasts its outcome and refreshes the rules; the caller
 * also shows a refusal's text on the field (`mutateAsync` rejects with the server's ApiError).
 */
export function useCreateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RuleInput) =>
      sendJson<{ id: string }>("POST", "/api/calendar/rules", input),
    onSuccess: async () => {
      toast.success("Rule added");
      await queryClient.invalidateQueries({ queryKey: calendarKeys.rules });
    },
    onError: (err) => {
      toast.error(err.message);
    },
    retry: false,
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RuleUpdate) =>
      sendJson<{ id: string }>("PUT", "/api/calendar/rules", input),
    onSuccess: async () => {
      toast.success("Rule saved");
      await queryClient.invalidateQueries({ queryKey: calendarKeys.rules });
    },
    onError: (err) => {
      toast.error(err.message);
    },
    retry: false,
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      sendJson<{ id: string }>("POST", "/api/calendar/rules/delete", { id }),
    onSuccess: async () => {
      toast.success("Rule deleted");
      await queryClient.invalidateQueries({ queryKey: calendarKeys.rules });
    },
    onError: (err) => {
      toast.error(err.message);
    },
    retry: false,
  });
}

/** `path` (a same-site path with an optional query) with `name=value` set. */
export function withParam(path: string, name: string, value: string): string {
  const url = new URL(path, "http://app.local");
  url.searchParams.set(name, value);
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Connect calendar: Better Auth sends the window to Microsoft to grant calendar read, then back
 * to `returnTo` (with `?calendar=error` when it failed). Only a failure to start comes back here.
 */
export async function connectCalendar(returnTo: string): Promise<void> {
  const { error } = await authClient.linkSocial({
    provider: "microsoft",
    scopes: [...CALENDAR_SCOPES],
    callbackURL: returnTo,
    errorCallbackURL: withParam(returnTo, "calendar", "error"),
  });
  if (error) throw new Error(error.message ?? "Couldn't start Connect calendar");
}

/**
 * The Connect calendar button. In its own tab the window goes to Microsoft and back to
 * `returnTo()` (`connectCalendar`). Inside Helm's frame Microsoft refuses to load, so it runs in
 * a popup instead (`POPUP_CONNECT_CALENDAR_PATH`); when the popup reports back or closes, the
 * calendar is re-read, and that read, not the message, decides what the page shows.
 */
export function useConnectCalendar(returnTo: () => string) {
  const queryClient = useQueryClient();
  // Better Auth sends the window to Microsoft on success, so this stays pending until the page
  // leaves. Only a failure to start comes back here.
  const redirect = useMutation({
    mutationFn: () => connectCalendar(returnTo()),
    onError: (err) => toast.error(err.message),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  const popup = usePopup({
    onReport: (error) => {
      if (error === null) toast.success("Calendar connected");
      else toast.error("Calendar not connected", { id: "calendar-error" });
      void refresh();
    },
    onClosed: () => void refresh(),
  });

  function connect() {
    if (!isEmbedded()) {
      redirect.mutate();
      return;
    }
    if (!popup.open(POPUP_CONNECT_CALENDAR_PATH, "employee-dashboard-calendar")) {
      toast.error("Your browser blocked the Microsoft window. Allow pop-ups for this site.", {
        id: "calendar-popup-blocked",
      });
    }
  }

  return { connect, pending: redirect.isPending || popup.waiting };
}
